import React, {FunctionComponent} from "react";
import {LocalizeContextProps, withLocalize} from "react-localize-redux";
import {BooleanInputField} from "../../../../common/components/input-fields/BooleanInputField";
import DropdownInputField from "../../../../common/components/input-fields/DropdownInputField";
import InputField from "../../../../common/components/input-fields/InputField";
import {SelectableItem} from "../../../../common/models/SelectableItem";
import SubstatusesInputField from "../../../../common/components/input-fields/SubstatusesInputField";
import {getEnumTranslationKey} from "../../../../common/helpers/getTranslationKey";
import mapEnumToSelectableItems from "../../../../common/helpers/mapEnumToSelectableItems";
import DateTimeInputField, {
    DateTimeInputFieldKind
} from "../../../../common/components/input-fields/DateTimeInputField";
import {isValueSet} from "../../../../common/helpers/isValueSet";
import CustomerAdvisorsInputField from "../../../../common/components/input-fields/CustomerAdvisorsInputField";
import CustomersFilterPreview from "./CustomersFilterPreview";
import {MarketingListCustomersFilter} from "../../../model/MarketingListCustomersFilter";
import CustomersFilter from "./CustomersFilter";
import StringCollectionInputField from "../../../../common/components/input-fields/StringCollectionInputField";
import {MultiSelect} from "../../../../common/components/MultiSelect";
import {MarketingListCustomerStatus} from "../../../model/MarketingListCustomerStatus";
import {ApplicationStatus} from "../../../../common/models/ApplicationStatus";
import NumberInputField, {NumberInputFieldKind} from "../../../../common/components/input-fields/NumberInputField";

export interface CustomersFilterProps extends LocalizeContextProps {
    editMode: boolean;
    filter?: MarketingListCustomersFilter;
    countryId: number;
    onChange: (filter: Partial<MarketingListCustomersFilter>) => void;
}

const CustomersFilterContainer: FunctionComponent<CustomersFilterProps> = (props) =>
    props.editMode
        ? <CustomersFilterPreview {...props} />
        : <CustomersFilter {...props} />

export const renderCustomerAdvisorsFilter = (props: CustomersFilterProps) => {
    const customerAdvisors = getFilterProp<number[]>('customerAdvisors', props) || [];
    const onCustomerAdvisorsChanged = (customerAdvisors: number[]) => props.onChange({ customerAdvisors });
    return (
        <div>
            <CustomerAdvisorsInputField
                descriptionKey="CUSTOMER_ADVISOR"
                editMode={true}
                className={customerAdvisors.length ? 'filter-active' : ''}
                customerAdvisorIds={customerAdvisors}
                onCustomerAdvisorsChanged={onCustomerAdvisorsChanged}
                placeholder={props.translate('HEADER.FILTER_CUSTOMER_ADVISORS').toString()}
            />
        </div>
    );
}

export const renderLoanStatusFilter = (props: CustomersFilterProps) => (
    renderDropdownInputFieldFilter(
        'lastApplicationStatus',
        'LOAN_STATUS.LABEL',
        mapEnumToSelectableItems(
            ApplicationStatus,
            item => props.translate(getEnumTranslationKey(ApplicationStatus, item, 'LOAN_STATUS', 'ALL')).toString(),
            true
        ),
        props
    )
);

export const renderCustomerStatusesFilter = (props: CustomersFilterProps) => {
    const statuses = getFilterProp<MarketingListCustomerStatus[]>('customerStatuses', props) || [];
    const onCustomerStatusesChanged = (customerStatuses: MarketingListCustomerStatus[]) => props.onChange({ customerStatuses });
    const customerStatusesItems = mapEnumToSelectableItems<number>(
        MarketingListCustomerStatus,
        item => `${props.translate('CUSTOMER_STATUS.PREFIX').toString()}${props.translate(getEnumTranslationKey(MarketingListCustomerStatus, item, 'CUSTOMER_STATUS')).toString()}`,
        false,
        true
    );

    return (
        <div>
            <MultiSelect
                descriptionKey="CUSTOMER_STATUS.LABEL"
                className={statuses.length ? 'filter-active' : ''}
                selectedItems={customerStatusesItems.filter(item => statuses.some(status => item.id === status))}
                items={customerStatusesItems}
                onSelectionChanged={items => onCustomerStatusesChanged(items.map(item => item.id as number))}
                placeHolder={props.translate('FILTER_CUSTOMER_STATUS').toString()}
            />
        </div>
    );
}

export const renderLastStatusChangeFromFilter = (props: CustomersFilterProps) =>
    renderDateTimeInputFieldFilter('lastStatusUpdateFrom', 'CAMPAIGNS_VIEW.LAST_STATUS_UPDATE_FROM', props);

export const renderLastStatusChangeToFilter = (props: CustomersFilterProps) =>
    renderDateTimeInputFieldFilter('lastStatusUpdateTo', 'CAMPAIGNS_VIEW.LAST_STATUS_UPDATE_TO', props);

export const renderLastContactFilter = (props: CustomersFilterProps) =>
    renderNumberInputFieldFilter('lastContact', 'MARKETING_LISTS_VIEW.LAST_CONTACT_SINCE_MONTHS', props, props.countryId, NumberInputFieldKind.Months);

export const renderMinimumGrossYearlyIncomeFilter = (props: CustomersFilterProps) =>
    renderNumberInputFieldFilter('minimumGrossYearlyIncome', 'MARKETING_LISTS_VIEW.MINIMUM_GROSS_YEARLY_INCOME', props, props.countryId, NumberInputFieldKind.MoneyPerYear);

export const renderPropertyEstimatedValueFilter = (props: CustomersFilterProps) => {
    if (!props.filter?.hasProperty) {
        return null;
    }
    return renderNumberInputFieldFilter('propertyEstimatedValue', 'MARKETING_LISTS_VIEW.PROPERTY_ESTIMATED_VALUE', props, props.countryId, NumberInputFieldKind.Money);
}
export const renderPropertyAreaMarketScoreFilter = (props: CustomersFilterProps) => {
    if (!props.filter?.hasProperty) {
        return null;
    }
    return renderNumberInputFieldFilter('propertyAreaMarketScore', 'MARKETING_LISTS_VIEW.PROPERTY_AREA_MARKET_SCORE', props, props.countryId, NumberInputFieldKind.NumberOf);
}
    
export const renderPaidAmountFromFilter = (props: CustomersFilterProps) => {
    if (props.filter?.lastApplicationStatus !== ApplicationStatus.PAID) {
        return null;
    }
    return renderNumberInputFieldFilter('paidAmountFrom', 'MARKETING_LISTS_VIEW.PAID_AMOUNT_FROM', props, props.countryId, NumberInputFieldKind.Money);
}
    
export const renderPaidAmountToFilter = (props: CustomersFilterProps) => {
    if (props.filter?.lastApplicationStatus !== ApplicationStatus.PAID) {
        return null;
    }
    return renderNumberInputFieldFilter('paidAmountTo', 'MARKETING_LISTS_VIEW.PAID_AMOUNT_TO', props, props.countryId, NumberInputFieldKind.Money);
}

export const renderNominalInterestFromFilter = (props: CustomersFilterProps) => {
    if (props.filter?.lastApplicationStatus !== ApplicationStatus.PAID) {
        return null;
    }
    return renderNumberInputFieldFilter('nominalInterestFrom', 'MARKETING_LISTS_VIEW.NOMINAL_INTEREST_FROM', props, props.countryId, NumberInputFieldKind.Percent, 0.01);
}

export const renderNominalInterestToFilter = (props: CustomersFilterProps) => {
    if (props.filter?.lastApplicationStatus !== ApplicationStatus.PAID) {
        return null;
    }
    return renderNumberInputFieldFilter('nominalInterestTo', 'MARKETING_LISTS_VIEW.NOMINAL_INTEREST_TO', props, props.countryId, NumberInputFieldKind.Percent, 0.01);
}
export const renderUnsecuredDebtsFromFilter = (props: CustomersFilterProps) =>
    renderNumberInputFieldFilter('unsecuredDebtsFrom', 'MARKETING_LISTS_VIEW.UNSECURED_DEBTS_FROM', props, props.countryId, NumberInputFieldKind.Money)

export const renderUnsecuredDebtsToFilter = (props: CustomersFilterProps) =>
    renderNumberInputFieldFilter('unsecuredDebtsTo', 'MARKETING_LISTS_VIEW.UNSECURED_DEBTS_TO', props, props.countryId, NumberInputFieldKind.Money)

export const renderHasClawbackFilter = (props: CustomersFilterProps) => {
    if (props.filter?.lastApplicationStatus !== ApplicationStatus.PAID) {
        return null;
    }
    return renderBooleanInputFieldFilter('hasClawback', 'MARKETING_LISTS_VIEW.HAS_CLAWBACK', props);
}

export const renderDateTimeInputFieldFilter = (name: keyof MarketingListCustomersFilter, descriptionKey: string, props: CustomersFilterProps) => {
    const value = getFilterProp<string | null>(name, props);
    if (!props.editMode && !isValueSet(value)) {
        return null;
    }

    const onValueChanged = (value: string | null | undefined) => props.onChange({ [name]: value });
    return (
        <div>
            <DateTimeInputField
                name={name}
                editMode={props.editMode}
                onValueChanged={value => onValueChanged(value ? value.toISOString() : undefined)}
                value={value ? new Date(value as string) : undefined}
                descriptionKey={descriptionKey}
                kind={DateTimeInputFieldKind.Date}
            />
        </div>
    )
}

export const renderNumberInputFieldFilter = (name: keyof MarketingListCustomersFilter, descriptionKey: string, props: CustomersFilterProps, countryId: number, kind: NumberInputFieldKind, step?: number) => {
    const value = getFilterProp<number | null>(name, props);
    if (!props.editMode && !isValueSet(value)) {
        return null;
    }

    const onValueChanged = (value: number | null | undefined) => props.onChange({ [name]: value });
    return (
        <div>
            <NumberInputField
                name={name}
                editMode={props.editMode}
                onValueChanged={onValueChanged}
                value={value}
                descriptionKey={descriptionKey}
                kind={kind}
                countryId={countryId}
                readonlyPrecision={0}
                step={step}
            />
        </div>
    );
};

export const renderBooleanInputFieldFilter = (name: keyof MarketingListCustomersFilter, descriptionKey: string, props: CustomersFilterProps) => {
    const value = getFilterProp<boolean>(name, props);
    if (!props.editMode && !isValueSet(value)) {
        return null;
    }

    const onValueChanged = (value: boolean | undefined) => props.onChange({ [name]: value });
    return (
        <div>
            <BooleanInputField
                name={name}
                editMode={props.editMode}
                onValueChanged={onValueChanged}
                value={value}
                descriptionKey={descriptionKey}
            />
        </div>
    )
}

export const renderSubstatusesInputFieldFilter = (name: keyof MarketingListCustomersFilter, labelKey: string, props: CustomersFilterProps) => {
    const value = getFilterProp<number[]>(name, props) || [];
    if (!props.editMode && (!value || !value.length)) {
        return null;
    }

    const onValueChanged = (value: number[]) => props.onChange({ [name]: value });
    if (props.editMode) {
        return (
            <div>
                <SubstatusesInputField
                    descriptionKey={labelKey}
                    editMode={true}
                    className={value.length ? 'filter-active' : ''}
                    substatusIds={value}
                    onSubstatusesChanged={onValueChanged}
                    placeholderKey={labelKey}
                />
            </div>
        )
    } else {
        return (
            <div>
                <InputField descriptionKey={labelKey} className="substatuses-input-field-filter">
                    <SubstatusesInputField substatusIds={value} />
                </InputField>
            </div>
        )
    }
}

export const renderDropdownInputFieldFilter = <T extends unknown>(name: keyof MarketingListCustomersFilter, descriptionKey: string, items: SelectableItem<number | string>[], props: CustomersFilterProps) => {
    const value = getFilterProp<T>(name, props);
    if (!props.editMode && !isValueSet(value)) {
        return null;
    }
    const onValueChanged = (value: T | undefined) => props.onChange({ [name]: value });
    
    return (
        <div>
            <DropdownInputField
                className={isValueSet(value as any) ? 'filter-active' : ''}
                descriptionKey={descriptionKey}
                editMode={props.editMode}
                name={name}
                value={value as any}
                onValueChanged={value => onValueChanged(value as T | undefined)}
                items={items}
            />
        </div>
    )
}

export const renderStringCollectionInputFilter = (name: keyof MarketingListCustomersFilter, labelKey: string, props: CustomersFilterProps) => {
    const value = getFilterProp<string[]>(name, props) || [];
    if (!props.editMode && (!value || !value.length)) {
        return null;
    }

    return (
        <StringCollectionInputField
            value={value}
            name={name}
            description={labelKey}
            edit={props.editMode}
            className={value.length ? 'filter-active' : ''}
            onValueChange={value => props.onChange({ [name]: value })}
        />
    )
}

export const getFilterProp = <T extends unknown>(name: keyof MarketingListCustomersFilter, props: CustomersFilterProps): T | null => {
    if (props.filter && name in props.filter) {
        return props.filter[name] as T;
    }

    return null;
}

export default withLocalize(CustomersFilterContainer);